/**
 * @author      Dario Casertano <dario@casertano.name>
 * @copyright   Copyright (c) 2023 Casertano Dario – All rights reserved.
 * @license     Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International.
 *
 * @link https://github.com/axios/axios#request-config
 */

import { settings } from "@/libraries/storage";
import { router } from "@/router";
import AppStore from "@/vuex/stores/AppStore";
import { default as _axios, InternalAxiosRequestConfig } from "axios";
import { AxiosResponse } from "axios";
import { assign, includes } from "lodash";
import { DateTime } from "luxon";
import { store as Vuex } from '@/vuex';

const {
    VUE_APP_API,
} = process.env

export const CancelToken = _axios.CancelToken;

export const axios = _axios.create({
    baseURL: VUE_APP_API,
})

axios.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    if (config.url?.endsWith('/licenses.txt')) {
        return config
    }

    config.headers = assign({}, config.headers, {
        'Accept': 'application/json',
        'Accept-Language': AppStore.state.appLanguage,
        'Content-Type': 'application/json',
        'x-sender': settings.get('system', 'app', 'uuid'),
        'x-timezone': DateTime.local().zoneName,
    })

    if (settings.has('auth', 'user', 'token')) {
        config.headers = assign(config.headers, {
            'Authorization': `Bearer ${settings.get('auth', 'user', 'token')}`,
        })
    }
    if (settings.has('auth', 'user', 'strategist')) {
        config.headers = assign(config.headers, {
            'x-strategist': settings.get('auth', 'user', 'strategist'),
        })
    }

    return config
}, (error) => Promise.reject(error))

let debounce = 0

axios.interceptors.response.use(async (response: AxiosResponse): Promise<AxiosResponse> => {
    if (response.headers[ 'x-api-version' ]) {
        settings.set('system', 'pwa', 'apiVersion', response.headers[ 'x-api-version' ])
    }

    if (response.headers[ 'x-auth' ] &&
        response.headers[ 'x-auth-data' ] &&
        (Date.now() >= (debounce + 1_000))
    ) {
        debounce = Date.now()

        settings.set('auth', 'user', 'auth', response.headers[ 'x-auth' ])
        Vuex.commit('AclStore/setUserData', JSON.parse(response.headers[ 'x-auth-data' ]))
    }

    if (response.status === 200) {
        const {
            code,
        } = response.data

        if (includes([
            401,
            423,
            498,
            499,
        ], code)) {
            await router.push('/login')
        }
    }

    return response
}, error => Promise.reject(error))

export interface ApiResponseLight {
    code: number;
    sign: string;
    text: string;
}

export interface ApiResponse<T> extends ApiResponseLight {
    pyld: T
}

export interface ApiResponsePaginator<T> extends ApiResponseLight {
    pyld: {
        paginator: {
            count: number
            maxPage: number
        }

        recordset: T[]
    }
}

export interface ApiResponseFormError extends ApiResponseLight {
    pyld: Record<string, string>
}
